import React, { useState, useEffect } from "react";
import { Select, DatePicker, Input } from "antd";
import type { DatePickerProps } from "antd";
import dayjs from "dayjs";
import { validateField } from "../utils/validation";
import "../styles/DynamicFields.css";

const { Option } = Select;

interface FormField {
  id: number;
  label: string;
  type: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface LocationData {
  countries: any[];
  states: any[];
  cities: any[];
  districts: any[];
}

interface DynamicFieldProps {
  field: FormField;
  value: any;
  error?: string;
  onChange: (fieldId: number, value: any, error?: string) => void;
  locationData?: LocationData;
}

const DynamicField: React.FC<DynamicFieldProps> = ({
  field,
  value,
  error,
  onChange,
  locationData,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");

  const [filteredStates, setFilteredStates] = useState<any[]>([]);
  const [filteredCities, setFilteredCities] = useState<any[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<any[]>([]);

  // Filter states based on country
  useEffect(() => {
    if (selectedCountry && locationData) {
      const states = locationData.states.filter(
        (s: any) =>
          s.country_id === selectedCountry || s.country === selectedCountry
      );
      setFilteredStates(states);
      setFilteredCities([]);
      setFilteredDistricts([]);
      setSelectedState("");
      setSelectedCity("");
      setSelectedDistrict("");
    }
  }, [selectedCountry, locationData]);

  // Filter cities based on state
  useEffect(() => {
    if (selectedState && locationData) {
      const cities = locationData.cities.filter(
        (c: any) => c.state_id === selectedState || c.state === selectedState
      );
      setFilteredCities(cities);
      setFilteredDistricts([]);
      setSelectedCity("");
      setSelectedDistrict("");
    }
  }, [selectedState, locationData]);

  // Filter districts based on city
  useEffect(() => {
    if (selectedCity && locationData) {
      const districts = locationData.districts.filter(
        (d: any) => d.city_id === selectedCity || d.city === selectedCity
      );
      setFilteredDistricts(districts);
      setSelectedDistrict("");
    }
  }, [selectedCity, locationData]);

  const handleChange = (val: string) => {
    const errorMsg = validateField(field, val);
    onChange(field.id, val, errorMsg);
  };

  const handleDateChange: DatePickerProps["onChange"] = (date, dateString) => {
    const val = typeof dateString === "string" ? dateString : "";
    handleChange(val);
  };

  useEffect(() => {
    if (field.type === "country") {
      const locationValue = {
        country: selectedCountry,
        state: selectedState,
        city: selectedCity,
        district: selectedDistrict,
      };

      const errorMsg = validateField(field, locationValue);
      onChange(field.id, locationValue, errorMsg);
    }
  }, [selectedCountry, selectedState, selectedCity, selectedDistrict]);

  const isFullWidth = field.type === "country";

  const renderInput = () => {
    switch (field.type) {
      case "text":
        return (
          <Input
            size="large"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            status={error ? "error" : ""}
          />
        );

      case "tel":
        return (
          <Input
            size="large"
            type="tel"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            status={error ? "error" : ""}
          />
        );

      case "date":
        return (
          <DatePicker
            size="large"
            value={value ? dayjs(value) : null}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            placeholder="Select date"
            status={error ? "error" : ""}
            style={{ width: "100%" }}
          />
        );

      case "select":
        return (
          <Select
            size="large"
            value={value || undefined}
            onChange={(val) => handleChange(val)}
            placeholder="Select an option"
            status={error ? "error" : ""}
            style={{ width: "100%" }}>
            {field.options?.map((option, idx) => (
              <Option key={idx} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        );

      case "country":
        return (
          <div className="location-grid">
            <div className="location-field">
              <label className="sub-label">
                Country<span className="required-mark">*</span>
              </label>
              <Select
                size="large"
                value={selectedCountry || undefined}
                onChange={(val) => {
                  setSelectedCountry(val);
                }}
                placeholder="Select Country"
                status={error ? "error" : ""}
                style={{ width: "100%" }}
                showSearch
                filterOption={(input: any, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }>
                {locationData?.countries.map((country: any) => (
                  <Option key={country.id} value={country.id}>
                    {country.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="location-field">
              <label className="sub-label">
                State<span className="required-mark">*</span>
              </label>
              <Select
                size="large"
                value={selectedState || undefined}
                onChange={(val) => {
                  setSelectedState(val);
                }}
                placeholder="Select State"
                disabled={!selectedCountry}
                style={{ width: "100%" }}
                showSearch
                filterOption={(input: any, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }>
                {filteredStates.map((state: any) => (
                  <Option key={state.id} value={state.id}>
                    {state.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="location-field">
              <label className="sub-label">
                City<span className="required-mark">*</span>
              </label>
              <Select
                size="large"
                value={selectedCity || undefined}
                onChange={(val) => {
                  console.log("Value: ", val);
                  setSelectedCity(val);
                }}
                placeholder="Select City"
                disabled={!selectedState}
                style={{ width: "100%" }}
                showSearch
                filterOption={(input: any, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }>
                {filteredCities.map((city: any) => (
                  <Option key={city.id} value={city.id}>
                    {city.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="location-field">
              <label className="sub-label">District</label>
              <Select
                size="large"
                value={selectedDistrict || undefined}
                onChange={(val) => {
                  setSelectedDistrict(val);
                }}
                placeholder="Select District"
                disabled={!selectedCity}
                style={{ width: "100%" }}
                showSearch
                filterOption={(input: any, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }>
                {filteredDistricts.map((district: any) => (
                  <Option key={district.id} value={district.id}>
                    {district.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`field-wrapper ${isFullWidth ? "full-width" : ""}`}>
      <label className="field-label">
        {field.type !== "country" && field.label}
        {field.required && field.type !== "country" && (
          <span className="required-mark">*</span>
        )}
      </label>
      {renderInput()}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
};

export default DynamicField;
